import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { activityApi } from "@/api";
import { colors, gradientBtnSx } from "./constants";
import { PageShell } from "./PageShell";
import { StatusCard } from "./StatusCard";

function useToken() {
  const params = new URLSearchParams(window.location.search);
  return params.get("token") || "";
}

/**
 * ActivityPageBase — 活动页通用逻辑基座
 *
 * @param {string}   activityType        - API 活动类型编码，如 "new_user_gift"
 * @param {string}   pageTitle           - document.title
 * @param {Function} renderCardContent   - ({ message, claimError }) => JSX
 *                                         渲染 Paper 卡片内的活动主体内容
 * @param {Function} renderSuccessCard   - ({ claimedAt, message }) => JSX
 *                                         渲染"已领取"状态的卡片
 * @param {string}   [claimButtonLabel]  - 领取按钮文字
 * @param {string}   [claimingLabel]     - 领取中按钮文字
 */
export function ActivityPageBase({
  activityType,
  pageTitle,
  renderCardContent,
  renderSuccessCard,
  claimButtonLabel = "立即领取",
  claimingLabel = "正在领取…",
}) {
  const token = useToken();

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [claimedAt, setClaimedAt] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const fetchStatus = useCallback(async () => {
    if (!token) {
      setStatus("no_token");
      return;
    }
    setStatus("loading");
    setClaimError("");
    try {
      const payload = await activityApi.getStatus(activityType, token);
      if (payload?.status === 1) {
        setStatus("claimed");
        setMessage(payload.message || "");
        setClaimedAt(payload.claimed_at || null);
      } else if (payload?.eligible) {
        setStatus("eligible");
        setMessage(payload.message || "");
        setClaimedAt(null);
      } else {
        setStatus("ineligible");
        setMessage(payload?.message || "您暂不符合领取条件");
        setClaimedAt(payload?.claimed_at || null);
      }
    } catch (error) {
      setStatus("error");
      setMessage(error.message || "网络异常，请检查网络连接后重试");
    }
  }, [token, activityType]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleClaim = async () => {
    setClaimError("");
    setClaiming(true);
    try {
      const { data: payload, message: responseMessage } = await activityApi.claim(
        activityType,
        token,
      );
      setStatus("claimed");
      setMessage(responseMessage || payload?.message || "领取成功！");
      setClaimedAt(payload?.claimed_at || new Date().toISOString());
    } catch (error) {
      setClaimError(error.message || "网络异常，请稍后重试");
    } finally {
      setClaiming(false);
    }
  };

  // ── 加载中 ──────────────────────────────────────────────
  if (status === "loading") {
    return (
      <PageShell>
        <CircularProgress size={48} sx={{ color: colors.gold }} />
        <Typography sx={{ mt: 2, color: "rgba(255, 248, 231, 0.82)" }}>
          正在为您点亮活动礼遇…
        </Typography>
      </PageShell>
    );
  }

  // ── 无 token ────────────────────────────────────────────
  if (status === "no_token") {
    return (
      <PageShell>
        <StatusCard
          icon={<ErrorOutlineIcon sx={{ fontSize: 56, color: colors.gold, mb: 2 }} />}
          title="链接无效"
        >
          <Typography sx={{ color: "rgba(255, 248, 231, 0.86)" }}>
            请通过知嘛小程序内的活动入口访问本页面
          </Typography>
        </StatusCard>
      </PageShell>
    );
  }

  // ── 加载错误 ────────────────────────────────────────────
  if (status === "error") {
    return (
      <PageShell>
        <StatusCard
          icon={<ErrorOutlineIcon sx={{ fontSize: 56, color: colors.gold, mb: 2 }} />}
          title="加载失败"
        >
          <Typography sx={{ color: "rgba(255, 248, 231, 0.82)", mb: 3 }}>
            {message}
          </Typography>
          <Button variant="contained" onClick={fetchStatus} sx={gradientBtnSx}>
            重新加载
          </Button>
        </StatusCard>
      </PageShell>
    );
  }

  // ── 已领取 ──────────────────────────────────────────────
  if (status === "claimed") {
    return (
      <PageShell>
        {renderSuccessCard({ claimedAt, message })}
      </PageShell>
    );
  }

  // ── 不符合条件 ──────────────────────────────────────────
  if (status === "ineligible") {
    return (
      <PageShell>
        <StatusCard
          icon={<ErrorOutlineIcon sx={{ fontSize: 56, color: colors.gold, mb: 2 }} />}
          title="暂不符合条件"
        >
          <Typography sx={{ color: "rgba(255, 248, 231, 0.82)" }}>
            {message}
          </Typography>
        </StatusCard>
      </PageShell>
    );
  }

  // ── 可领取 ──────────────────────────────────────────────
  return (
    <PageShell>
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 2,
          py: { xs: 5, sm: 6 },
          pb: { xs: 16, sm: 18 },
        }}
      >
        {renderCardContent({ message, claimError })}

        <Typography
          variant="caption"
          display="block"
          textAlign="center"
          sx={{ color: "rgba(255, 248, 231, 0.68)", mt: 2.5 }}
        >
          活动最终解释权归知嘛团队所有
        </Typography>

        {/* 固定底部领取按钮 */}
        <Box
          sx={{
            position: "fixed",
            left: "50%",
            bottom: { xs: 16, sm: 24 },
            transform: "translateX(-50%)",
            width: "100%",
            maxWidth: 600,
            px: { xs: 3, sm: 4 },
            zIndex: 4,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              pointerEvents: "auto",
              background:
                "linear-gradient(180deg, rgba(217, 31, 63, 0) 0%, rgba(217, 31, 63, 0.3) 20%, rgba(179, 15, 42, 0.82) 100%)",
              borderRadius: 4,
              pt: 3,
              pb: 0.5,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={claiming}
              onClick={handleClaim}
              startIcon={
                claiming ? (
                  <CircularProgress size={20} sx={{ color: colors.darkText }} />
                ) : (
                  <CardGiftcardIcon />
                )
              }
              sx={{
                ...gradientBtnSx,
                py: 1.8,
                fontSize: "1.08rem",
                borderRadius: 3,
              }}
            >
              {claiming ? claimingLabel : claimButtonLabel}
            </Button>
          </Box>
        </Box>
      </Container>
    </PageShell>
  );
}
